import * as React from "react";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { useOffer, useOfferMode } from "@nju/data-access-portal-offers";
import { useOrchestrators } from "@nju/data-access-portal-orchestrator";
import { portalRoutes } from "@nju/routes";
import { Layout, Main, Modal, useOverlay } from "@nju/ui";
import { CancelOrderModal } from "../cancelOrder";
import { consentsMachine } from "./consents.machine";
import { ConsentsForm } from "./partials/ConsentsForm";
import { DiscountWarningModal } from "./partials/DiscountWarningModal";

export function ConsentsPage() {
  const mode = useOfferMode();
  const router = useRouter();
  const consentsModal = useOverlay();
  const cancelOrderModal = useOverlay();
  const ftthOrchestrator = useOrchestrators("ftth", { isOptional: true });
  const multiOrderOrchestrator = useOrchestrators("multiOrder", {
    isOptional: true,
  });
  const selectedOrchestrator =
    mode === "ftth" ? ftthOrchestrator : multiOrderOrchestrator;
  const isPostpaid =
    mode === "mobile" &&
    multiOrderOrchestrator?.offerCategory === "MOBILE_POSTPAID";
  const orchestratorId = selectedOrchestrator?.id;
  const offer = useOffer(selectedOrchestrator?.offerId);

  const [current, send] = useMachine(consentsMachine, {
    context: {
      consentsCatalogue: [],
      orchestratorId,
      mode,
    },
    actions: {
      redirectToNextStep: () => {
        if (isPostpaid) {
          router.push(
            portalRoutes.umowy.get({
              mode,
            }),
          );
        } else {
          router.push(
            portalRoutes.podsumowanie.get({
              mode,
            }),
          );
        }
      },
      redirectToMyAccount: () => {
        router.push(
          portalRoutes.mojeKonto.index.get({
            mode,
          }),
        );
      },
    },
  });
  const { context, matches } = current;
  const isSaving = matches("submitting") || matches("setConsentsSuccess");

  React.useEffect(() => {
    if (selectedOrchestrator?.hasConsentsSet) {
      router.push(
        portalRoutes.podsumowanie.get({
          mode,
        }),
      );
    }
  }, [router, selectedOrchestrator?.hasConsentsSet, mode]);

  const selectConsent = React.useCallback(
    ({
      value,
      consentId,
      consentVersion,
    }: {
      value: boolean;
      consentId: string;
      consentVersion: number;
    }) => {
      send({
        type: "SELECT_CONSENT",
        consent: {
          consentId,
          consentVersion,
          accepted: value,
        },
      });
    },
    [send],
  );

  const selectAllConsents = (checked: boolean) => {
    send({ type: "SELECT_ALL_CONSENTS", value: checked });
  };

  const handleSubmit = React.useCallback(() => {
    if (
      context.consentsCatalogue.some((c) => {
        return !c.accepted;
      })
    ) {
      consentsModal.open();
      return;
    }
    send({ type: "SUBMIT" });
  }, [send, context.consentsCatalogue, consentsModal]);

  return (
    <Layout>
      <Main center={true}>
        <ConsentsForm
          activationFee={offer?.activationFee || 0}
          context={context}
          handleSubmit={handleSubmit}
          isSaving={isSaving}
          multiOrderItemsCount={multiOrderOrchestrator?.multiOrderItemsCount}
          onCancel={cancelOrderModal.open}
          selectAllConsents={selectAllConsents}
          selectConsent={selectConsent}
        />
        {consentsModal.isOpened && (
          <Modal onClose={consentsModal.close}>
            <DiscountWarningModal
              activationFee={offer?.activationFee || 0}
              activationFeeWithConsentsDiscount={
                offer?.activationFeeWithConsentsDiscount || 0
              }
              isSaving={isSaving}
              multiOrderItemsCount={
                multiOrderOrchestrator?.multiOrderItemsCount
              }
              onClose={consentsModal.close}
              onConfirm={() => {
                send({ type: "SUBMIT" });
              }}
            />
          </Modal>
        )}
        {cancelOrderModal.isOpened && orchestratorId && (
          <Modal onClose={cancelOrderModal.close} title="anulowanie zamówienia">
            <CancelOrderModal
              mode={mode}
              onClose={cancelOrderModal.close}
              orchestratorId={orchestratorId}
            />
          </Modal>
        )}
      </Main>
    </Layout>
  );
}
